/* Presentation bridge. Kaching alone owns selection, prices, discounts and cart submission. */
(() => {
  'use strict';
  const selector = '[data-thc-kaching="true"]';
  const instances = new Map();
  const parse = value => { try { return JSON.parse(value || '{}'); } catch { return {}; } };
  function init(root) {
    if (instances.has(root)) return;
    const mount = root.querySelector('[data-thc-kaching-mount]');
    const form = mount?.closest('form');
    if (!form) return;
    const controller = new AbortController();
    const options = {signal:controller.signal};
    const images = parse(root.querySelector('[data-thc-kaching-images]')?.textContent);
    const attached = new WeakSet();
    let queued = false, timer, lastQuantity = null, disposed = false;
    const setQuantity = quantity => {
      if (!Number.isFinite(quantity) || quantity < 1 || quantity === lastQuantity) return;
      lastQuantity = quantity;
    root.dataset.thcOfferIndex = ({1:0,3:1,5:2})[quantity] ?? 1;
      window.thcOffer?.(quantity, root);
    };
    function updateSummary(block) {
      const selected = block.querySelector('.kaching-bundles__bar--selected');
      if (!selected) return;
      const title = selected.querySelector('.kaching-bundles__bar-title')?.textContent.trim() || '';
      const price = selected.querySelector('.kaching-bundles__bar-price')?.textContent.trim() || '';
      const stickyTitle = root.querySelector('[data-thc-kaching-title]');
      const stickyPrice = root.querySelector('[data-thc-kaching-price]');
      if (stickyTitle && stickyTitle.textContent !== title) stickyTitle.textContent = title;
      if (stickyPrice && stickyPrice.textContent !== price) stickyPrice.textContent = price;
      // Read only the selected quantity. Never write to the app's inputs or configuration.
    const bars = [...block.querySelectorAll('.kaching-bundles__bar')];
    const quantity = Number(selected.dataset.thcJars) || [1, 3, 5][bars.indexOf(selected)] || 0;
      if (quantity > 0) setQuantity(quantity);
    }
    function sync() {
      queued = false;
      if (disposed) return;
      // Legacy embeds may mount beside the placeholder, inside the same product form.
      const blocks = form.querySelectorAll('kaching-bundles-block');
      let ready = false;
      blocks.forEach(block => {
        const bars = block.querySelectorAll('.kaching-bundles__bar');
        if (bars.length) ready = true;
        // The rendered web component exposes the deal definition in this attribute.
        // If Kaching changes it, keep its original images rather than guessing quantities.
        const definition = parse(block.getAttribute('deal-block'));
        const deals = Array.isArray(definition?.dealBars) ? definition.dealBars : [];
        bars.forEach(bar => {
          const id = bar.getAttribute('data-deal-bar-id') || bar.querySelector('input[type="radio"]')?.value;
          const deal = deals.find(d => String(d.id) === String(id));
          const quantity = deal?.dealBarType === 'quantity-break' ? Number(deal.quantity) : 0;
          if (quantity > 0) {
            if (bar.dataset.thcJars !== String(quantity)) bar.dataset.thcJars = String(quantity);
          } else delete bar.dataset.thcJars;
          const src = images[quantity];
          const main = bar.querySelector('.kaching-bundles__bar-main');
          if (main) {
            let img = main.querySelector('.thc-kaching-image');
            if (!src) {
              img?.remove();
              delete main.dataset.thcBundleImage;
              return;
            }
            if (!img) {
              img = document.createElement('img');
              // Own this presentation node; never compete with the app's image updates.
              img.className = 'thc-kaching-image';
              img.width = 90;
              img.height = 75;
              img.decoding = 'async';
              img.addEventListener('load',() => {main.dataset.thcBundleImage = 'true';},options);
              img.addEventListener('error',() => {main.dataset.thcBundleImage = 'error';},options);
              main.prepend(img);
            }
            if (img.getAttribute('src') !== src) {
              main.dataset.thcBundleImage = 'true';
              img.setAttribute('src',src);
            }
            const alt = quantity === 1 ? '1 pot The Healthy Cat' : quantity+' pots The Healthy Cat';
            if (img.alt !== alt) img.alt = alt;
          }
        });
        if (!attached.has(block)) {
          attached.add(block);
          block.addEventListener('deal-bar-selected',schedule,options);
          block.addEventListener('variant-selected',schedule,options);
          block.addEventListener('variants-changed',event => {
            schedule();
            const lines = event.detail?.variantIdQuantities;
            if (Array.isArray(lines)) {
              const total = lines.reduce((sum,line) => sum+(Number(line.quantity)||0),0);
              requestAnimationFrame(() => { if (!disposed) setQuantity(total); });
            }
          },options);
        }
        updateSummary(block);
      });
      root.dataset.thcKachingReady = String(ready);
      const status = mount.querySelector('[data-thc-kaching-status]');
      if (status) status.hidden = ready;
      if (ready) {
        clearTimeout(timer);
        const help = mount.querySelector('[data-thc-kaching-help]');
        if (help) help.hidden = true;
        const error = form.querySelector('.thc-product-error');
        if (error?.dataset.thcKachingError === 'true') { error.textContent=''; delete error.dataset.thcKachingError; }
      }
    }
    function schedule() {
      if (disposed || queued) return;
      queued = true;
      requestAnimationFrame(sync);
    }
    const observer = new MutationObserver(schedule);
    observer.observe(form,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['class','deal-block','data-deal-bar-id']});
    const offersPresent = () => root.dataset.thcKachingReady === 'true' && !!form.querySelector('kaching-bundles-block .kaching-bundles__bar');
    const guard = event => {
      if (offersPresent()) return;
      if (event.type === 'click' && !event.target.closest('[data-thc-kaching-submit]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.thcCheckout?.fail();
      const error = form.querySelector('.thc-product-error');
      if (error) {
        error.dataset.thcKachingError = 'true';
        error.textContent = 'Les offres sont en cours de chargement. Veuillez patienter, puis réessayer.';
      }
    };
    form.addEventListener('click',guard,{...options,capture:true});
    form.addEventListener('submit',guard,{...options,capture:true});
    // Standard Shopify AJAX form: Kaching can enrich this request with its complete bundle.
    // An app that handles submission itself keeps ownership via preventDefault().
    let submitting = false;
    form.addEventListener('submit',async event => {
      if (event.defaultPrevented || !offersPresent()) return;
      event.preventDefault();
      if (submitting) return;
      const button = form.querySelector('[data-thc-kaching-submit]');
      if (!button || button.disabled) return;
      submitting = true;
      window.thcCheckout?.begin(button);
      const data = new FormData(form);
      button.disabled = true;
      try {
        const base = window.Shopify?.routes?.root || '/';
        const response = await window.fetch(base+'cart/add.js',{method:'POST',headers:{Accept:'application/json'},body:data});
        const result = await response.json();
        if (!response.ok || Number(result.status)>=400) throw new Error(result.description || 'Impossible d’ajouter cette offre.');
        await window.clyroShop?.refresh();
        if (!window.thcCheckout?.isActive()) window.clyroShop?.openCart();
      } catch (error) {
        window.thcCheckout?.fail(error.message);
        const message = form.querySelector('.thc-product-error');
        if (message) message.textContent = error.message || 'Impossible d’ajouter cette offre. Veuillez réessayer.';
      } finally {submitting=false;button.disabled=false;}
    },options);
    timer = setTimeout(() => {
      if (root.dataset.thcKachingReady === 'true') return;
      const status = mount.querySelector('[data-thc-kaching-status]');
      if (status) status.textContent = 'Les offres sont momentanément indisponibles. Veuillez actualiser la page.';
      const help = mount.querySelector('[data-thc-kaching-help]');
      if (help) help.hidden = false;
    },8000);
    instances.set(root,() => {disposed=true;controller.abort();observer.disconnect();clearTimeout(timer);});
    sync();
  }
  function scan(scope=document) {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll(selector).forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => scan());
  else scan();
  document.addEventListener('shopify:section:load',event => scan(event.target));
  document.addEventListener('shopify:section:unload',event => {
    for (const [root,dispose] of instances) if (root === event.target || event.target.contains(root)) { dispose(); instances.delete(root); }
  });
})();

// Each bundle is a dedicated Shopify variant, so renewals keep the first-order price.
document.addEventListener('click', event => {
  const bar = event.target.closest?.('.kaching-bundles__bar');
  const root = bar?.closest?.('[data-thc-kaching="true"]');
  if (root) root.dataset.thcOfferIndex = [...root.querySelectorAll('.kaching-bundles__bar')].indexOf(bar);
}, true);
window.thcLastSelectedOfferIndex = 1;
document.addEventListener('click', event => {
  const offer = event.target.closest?.('button');
    if (!offer) return;
  const label = offer.textContent || '';
  if (/\b1 pot\b/i.test(label)) window.thcLastSelectedOfferIndex = 0;
  else if (/\b3 pots\b/i.test(label)) window.thcLastSelectedOfferIndex = 1;
  else if (/\b5 pots\b/i.test(label)) window.thcLastSelectedOfferIndex = 2;
}, true);
window.thcBundleAdd = async (event, button) => {
  event.preventDefault();
  event.stopImmediatePropagation();
  if (button.disabled) return;
  const root = button.closest('[data-thc-kaching="true"]');
  const bars = [...(root?.querySelectorAll('.kaching-bundles__bar') || [])];
  const selected = root?.querySelector('.kaching-bundles__bar[aria-checked="true"]');
  const chosenOffer = document.querySelector('.thc-offer.sel, .nc-off.sel, .ptt-bundle.sel, .kaching-bundles__bar--selected');
  const chosenText = chosenOffer?.textContent || '';
  const index = /5\s+pots/i.test(chosenText) ? 2 : /3\s+pots/i.test(chosenText) ? 1 : /1\s+pot/i.test(chosenText) ? 0 : Number(window.thcLastSelectedOfferIndex ?? 1);
  const variantIds = ['66056376779142', '67594596876678', '67594596909446'];
  const error = root?.querySelector('.thc-product-error');
  if (index < 0 || !variantIds[index]) {
    if (error) error.textContent = 'Veuillez sélectionner une offre.';
    return;
  }
  const checkbox = root.querySelector('.kaching-bundles__subscriptions__checkbox');
  const subscribed = root.querySelector('.kaching-bundles__bar[role="checkbox"][aria-checked="true"]') !== null;
  const planId = button.dataset.thcPlanId;
  if (subscribed && !planId) {
    if (error) error.textContent = 'Abonnement indisponible. Veuillez réessayer.';
    return;
  }
  const item = { id: Number(variantIds[index]), quantity: 1 };
  if (subscribed) item.selling_plan = Number(planId);
  button.disabled = true;
  if (error) error.textContent = '';
  try {
    const base = window.Shopify?.routes?.root || '/';
    const response = await fetch(base + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Impossible d’ajouter cette offre.');
    await window.clyroShop?.refresh?.();
    if (!window.thcCheckout?.isActive?.()) window.clyroShop?.openCart?.();
  } catch (e) {
    if (error) error.textContent = e.message || 'Impossible d’ajouter cette offre.';
  } finally {
    button.disabled = false;
  }
};

// Use the selected bundle variant before Kaching intercepts the add-to-cart button.
window.addEventListener('click', function (event) {
  const button = event.target.closest?.('button.nc-atc');
  if (!button || !button.closest('[data-thc-kaching="true"]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.thcBundleAdd(event, button);
}, true);
